import React, { useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { Autocomplete, Box, Tab, Typography } from '@mui/material'
import { useCurrentRoute, useRoutes } from '../store'
import { workflowService } from '../../../../services/admin/workflow'
import { useAuthContext } from '../../../../hooks/useAuthContext'
import DialogWrapper from '../../../../components/core-ui/DialogWrapper'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import AdvanceRouteConfiguration from './AdvanceRouteConfiguration'
import { adminTeamsService } from '../../../../services/admin/teams'
import { GroupHeader, GroupItems } from '../../components/CreateProcessTemplateDialog'
import { toast } from 'react-toastify'

const routeTypes = [
  { label: 'regress', value: 'regress' },
  { label: 'progress', value: 'progress' },
  { label: 'neutral', value: 'neutral' },
]

const CreateEditRouteDialog = ({ processTemplateId }) => {
  const { user } = useAuthContext()
  const [selectedTabValue, setSelectedTabValue] = React.useState('1')
  const route = useCurrentRoute((state) => state.currentRoute)
  const setCurrentRoute = useCurrentRoute((state) => state.setCurrentRoute)
  const setRoutes = useRoutes((state) => state.setRoutes)
  const routes = useRoutes((state) => state.routes)
  const [teams, setTeams] = useState([])
  const [hiddenForTeamIds, setHiddenForTeamIds] = useState([])

  const [routeType, setRouteType] = useState(null)
  const [routeName, setRouteName] = useState('')
  const [routeSequence, setRouteSequence] = useState('')

  useEffect(() => {
    const type = {
      label: route?.route?.route_type,
      value: route?.route?.route_type,
    }
    setRouteType(type)
    setRouteName(route?.label)
    setRouteSequence(route?.route?.sequence || '')
  }, [route])

  useEffect(() => {
    ;(async () => {
      if (route?.route) {
        const teams = await adminTeamsService.getTeams(user)
        setTeams(teams)

        const hiddenTeamMap = await workflowService.getHiddenRouteTeamMap(
          user,
          route?.route?.route_template_id,
        )
        setHiddenForTeamIds(hiddenTeamMap?.map((it) => it.initiator_team_id))
      }
    })()
  }, [route])

  const onSave = async () => {
    // save to database
    const updates = {
      route_type: routeType?.value,
      route_template_name: routeName,
      user_id: user?.id,
      sequence: routeSequence ? Number(routeSequence) : route?.route?.sequence,
    }
    await workflowService.updateRouteTemplate(user, route?.route?.route_template_id, updates)

    // update the local routes
    const updatedRoutes = routes?.map((it) => {
      return it.route_template_id == route?.route?.route_template_id ? { ...it, ...updates } : it
    })
    setRoutes(updatedRoutes)

    // finally hide dialog
    onHide()
  }

  const onHide = () => {
    setCurrentRoute(null)
    setRouteType(null)
  }

  const handleChange = (_, newValue) => {
    setSelectedTabValue(newValue)
  }

  const saveHiddenRouteChanges = async () => {
    // save hidden route team changes

    try {
      await workflowService.setHiddenRouteTeamMap(
        user,
        route?.route?.route_template_id,
        hiddenForTeamIds,
      )
      toast.success('Hidden teams updated successfully!')
    } catch (error) {
      toast.error('Something went wrong!')
    }
  }

  return (
    <DialogWrapper
      open={route != null}
      onClose={onHide}
      heading={'Configure Route'}
      maxWidth="md"
      fullWidth
    >
      <hr></hr>
      <Typography style={{ textAlign: 'center' }}>
        {route?.sourceActivity?.activity_template_name} →{' '}
        {route?.targetActivity?.activity_template_name}
      </Typography>
      <hr></hr>
      <TabContext value={selectedTabValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList
            onChange={handleChange}
            aria-label="Process Template Setup"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="General" value="1" />
            <Tab label="Advance" value="2" />
          </TabList>
        </Box>
        <TabPanel value="1" sx={{ padding: 0, paddingTop: 1 }}>
          <TextField
            size="small"
            style={{ margin: '8px 0px' }}
            id="outlined"
            label="Route Name"
            placeholder="Route Name"
            fullWidth
            onChange={(e) => setRouteName(e.target.value)}
            value={routeName}
          />
          <div style={{ display: 'flex', gap: '16px', marginTop: '8px', marginBottom: '8px' }}>
            <Autocomplete
              fullWidth
              size="small"
              id="route-type"
              options={routeTypes}
              value={routeType}
              defaultValue={routeTypes[2]}
              onChange={(e, v) => {
                if (v) {
                  setRouteType(v)
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Route Type" placeholder="Route Type" />
              )}
            />
            <TextField
              size="small"
              id="outlined-sequence"
              label="Sequence"
              placeholder="Sequence"
              type="number"
              fullWidth
              onChange={(e) => setRouteSequence(e.target.value)}
              value={routeSequence}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 16 }}>
            <Button variant="contained" onClick={onHide}>
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => onSave({ route, routeType, routeName })}
            >
              Save
            </Button>
          </div>
        </TabPanel>
        <TabPanel value="2" sx={{ padding: 0, paddingTop: 2 }}>
          <Typography variant="h">Hiding Route Based on Field Values:</Typography>
          <AdvanceRouteConfiguration route={route} processTemplateId={processTemplateId} />
          <hr style={{ marginTop: 16 }}></hr>
          <Typography variant="h">Hide For Team(s)</Typography>
          <Autocomplete
            style={{ marginTop: 12 }}
            multiple
            size="small"
            value={teams.filter((it) => hiddenForTeamIds.includes(it.id)) || []}
            options={teams.sort((a, b) => {
              if (a.team_type === 'internal') return -1
              if (b.team_type === 'internal') return 1
              return -b.team_type.localeCompare(a.team_type)
            })}
            groupBy={(option) => option.team_type}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => <TextField {...params} label="Select Team" />}
            renderGroup={(params) => {
              return (
                <li key={params.key}>
                  <GroupHeader>{params.group}</GroupHeader>
                  <GroupItems>{params.children}</GroupItems>
                </li>
              )
            }}
            onChange={(e, v) => {
              setHiddenForTeamIds(v.map((it) => it.id))
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 16 }}>
            <Button color="primary" variant="contained" onClick={saveHiddenRouteChanges}>
              Save Changes
            </Button>
          </div>
        </TabPanel>
      </TabContext>
    </DialogWrapper>
  )
}

export default CreateEditRouteDialog
